import { takeLatest, call, put } from 'redux-saga/effects';
import {
  loadPurchases,
  loadPurchasesSuccess,
  loadPurchasesError,
  savePurchaseRequest,
  payPurchaseRequest,
  cancelPurchaseRequest,
} from './actions';
import {
  listPurchases,
  createPurchase,
  payPurchase,
  deletePurchase,
} from '@/services/purchases';
import { getActiveShopId } from '@/utils/shopSession';
import { hydratePurchases } from '@/containers/posData/actions';
import type { Purchase, PurchaseItem } from '@/lib/types';

function mapPurchase(dto: any): Purchase {
  return {
    id: String(dto.id),
    vendorId: dto.vendorId != null ? String(dto.vendorId) : undefined,
    vendorName: dto.vendorName,
    items: dto.items || [],
    totalAmount: Number(dto.totalAmount),
    paidAmount: Number(dto.paidAmount),
    dueAmount: Number(dto.dueAmount),
    paymentMethod: dto.paymentMethod ?? null,
    paymentStatus: (dto.paymentStatus as Purchase['paymentStatus']) || 'unpaid',
    status: (dto.status as Purchase['status']) || 'completed',
    createdAt: dto.createdAt,
    settledAt: dto.settledAt,
  };
}

export function* handleLoadPurchases(): Generator<any, void, any> {
  try {
    const shopId = getActiveShopId();
    if (!shopId) {
      yield put(loadPurchasesError('No active shop selected'));
      return;
    }
    const raw: any[] = yield call(listPurchases, shopId);
    const purchases = raw.map(mapPurchase);
    yield put(hydratePurchases(purchases));
    yield put(loadPurchasesSuccess(purchases));
  } catch (e: any) {
    yield put(loadPurchasesError(e?.message || 'Failed to load purchases'));
  }
}

export function* handleSavePurchase(
  action: ReturnType<typeof savePurchaseRequest>
): Generator<any, void, any> {
  try {
    const shopId = getActiveShopId();
    if (!shopId) {
      yield put(loadPurchasesError('No active shop selected'));
      return;
    }
    const { vendorId, items, paidAmount, paymentMethod } = action.payload.purchase;
    yield call(createPurchase, shopId, {
      vendorId,
      items: items as PurchaseItem[],
      paidAmount,
      paymentMethod,
    });
    yield put(loadPurchases());
  } catch (e: any) {
    yield put(loadPurchasesError(e?.message || 'Failed to save purchase'));
  }
}

export function* handlePayPurchase(
  action: ReturnType<typeof payPurchaseRequest>
): Generator<any, void, any> {
  try {
    const shopId = getActiveShopId();
    if (!shopId) return;
    const { purchaseId, amount, method } = action.payload;
    yield call(payPurchase, shopId, Number(purchaseId), amount, method);
    yield put(loadPurchases());
  } catch (e: any) {
    yield put(loadPurchasesError(e?.message || 'Failed to record payment'));
  }
}

export function* handleCancelPurchase(
  action: ReturnType<typeof cancelPurchaseRequest>
): Generator<any, void, any> {
  try {
    const shopId = getActiveShopId();
    if (!shopId) return;
    yield call(deletePurchase, shopId, Number(action.payload.id));
    yield put(loadPurchases());
  } catch (e: any) {
    yield put(loadPurchasesError(e?.message || 'Failed to cancel purchase'));
  }
}

export default function* saga() {
  yield takeLatest(loadPurchases.type, handleLoadPurchases);
  yield takeLatest(savePurchaseRequest.type, handleSavePurchase);
  yield takeLatest(payPurchaseRequest.type, handlePayPurchase);
  yield takeLatest(cancelPurchaseRequest.type, handleCancelPurchase);
}
